import { useEffect } from "react";

interface SEOOptions {
  title?: string;
  description?: string;
  ogImage?: string;
  canonical?: string;
  noIndex?: boolean;
}

export function useSEO({ title, description, ogImage, canonical, noIndex }: SEOOptions) {
  useEffect(() => {
    const prev = {
      title: document.title,
      desc: getMetaContent("description"),
      ogTitle: getMetaContent("og:title", true),
      ogDesc: getMetaContent("og:description", true),
      ogImage: getMetaContent("og:image", true),
      twTitle: getMetaContent("twitter:title", true),
      twDesc: getMetaContent("twitter:description", true),
    };

    const fullTitle = title ? title + " — 9nd.org" : "9nd.org — Powerful Tools, Simplified";

    document.title = fullTitle;
    setMeta("description", description || "");
    setMetaOG("og:title", fullTitle);
    setMetaOG("og:description", description || "");
    setMetaOG("twitter:title", fullTitle);
    setMetaOG("twitter:description", description || "");
    if (ogImage) setMetaOG("og:image", ogImage);

    let canonicalEl = document.querySelector<HTMLLinkElement>("link[rel='canonical']");
    if (canonical) {
      if (!canonicalEl) {
        canonicalEl = document.createElement("link");
        canonicalEl.rel = "canonical";
        document.head.appendChild(canonicalEl);
      }
      canonicalEl.href = canonical;
    }

    let robotsEl = document.querySelector<HTMLMetaElement>("meta[name='robots']");
    if (noIndex) {
      if (!robotsEl) {
        robotsEl = document.createElement("meta");
        robotsEl.name = "robots";
        document.head.appendChild(robotsEl);
      }
      robotsEl.content = "noindex,nofollow";
    }

    return () => {
      document.title = prev.title;
      if (prev.desc) setMeta("description", prev.desc);
      if (prev.ogTitle) setMetaOG("og:title", prev.ogTitle);
      if (prev.ogDesc) setMetaOG("og:description", prev.ogDesc);
      if (prev.ogImage) setMetaOG("og:image", prev.ogImage);
      if (prev.twTitle) setMetaOG("twitter:title", prev.twTitle);
      if (prev.twDesc) setMetaOG("twitter:description", prev.twDesc);
      if (noIndex && robotsEl) robotsEl.remove();
    };
  }, [title, description, ogImage, canonical, noIndex]);
}

function getMetaContent(nameOrProp: string, isOG = false): string {
  const attr = isOG ? `property` : `name`;
  return document.querySelector<HTMLMetaElement>(`meta[${attr}='${nameOrProp}']`)?.content || "";
}

function setMeta(name: string, content: string) {
  let el = document.querySelector<HTMLMetaElement>(`meta[name='${name}']`);
  if (!el) {
    el = document.createElement("meta");
    el.name = name;
    document.head.appendChild(el);
  }
  el.content = content;
}

function setMetaOG(property: string, content: string) {
  let el = document.querySelector<HTMLMetaElement>(`meta[property='${property}']`);
  if (!el) {
    el = document.createElement("meta");
    el.setAttribute("property", property);
    document.head.appendChild(el);
  }
  el.content = content;
}
